import React, { createRef, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useSnackbar } from 'notistack'
import { TabContext } from '@mui/lab'
import { Tab, Tabs, Tooltip, Typography } from '@mui/material'
import { createStyles, makeStyles } from '@mui/styles'

import { flowElementTypes, flowTaskTabNames, pageTypes } from '../../../../../../constants'
import { updateFlowTask, assignFlowTask, cacheUpdateFlowGroupTask } from '../../../../../../actions'
import { checkErrorResponse, checkUpdateResponse, DebugView, Logger, LoggerTypes } from '../../../../../../utils'
import { iconNames, Icon, TabPanel, Accordion, AccordionSummary, AccordionDetails } from '../../../../../../components'

import { FlowElementTaskAssignPanel } from '../FlowElementTaskAssignPanel'
import { FlowElementTaskAboutPanel } from '../FlowElementTaskAboutPanel'
import { FlowElementTaskGeographyPanel } from '../FlowElementTaskGeographyPanel'
import { FlowElementTaskInteractionResourcesPanel } from '../FlowElementTaskInteractionResourcesPanel'
import { FlowElementTaskInteractionVariablesPanel } from '../FlowElementTaskInteractionVariablesPanel'
import { FlowElementTaskParameterPanel } from '../FlowElementTaskParameterPanel'
import '../../../common-elements-style.scss'
import './style.scss'
import { FlowElementTaskInteractionCheckPanel } from '../FlowElementTaskInteractionCheckPanel'

const useStyles = makeStyles((theme) =>
  createStyles({
    accordionError: {
      backgroundColor: theme.palette.error.light
    },
    pinError: {
      backgroundColor: theme.palette.error.main
    }
  })
)

/**
 * @returns {div}
 */
export const FlowElementTaskTab = ({
  currentProject,
  currentScenario,
  currentFlowGroup,
  currentElement,
  // -- Scenario's Attributes
  resources,
  variables,
  locations,
  weather_data,
  parameters,
  products,
  // products,
  // -- Flow's Actions + AlphaFactors
  alphaFactors,
  tasks,
  // -- refresh after saving
  refreshAfterSave
}) => {
  const [tabValue, setTabValue] = useState(0)
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { enqueueSnackbar } = useSnackbar()
  const styleClasses = useStyles()
  const ref = createRef()

  // -- This variable is used to temporary locally save the changing task data between accordions
  // -- to be able to save in one place even if changes have been made in different accordion's forms.
  const [taskInstanceTemp, setTaskInstanceTemp] = useState(null)
  const [accordionExpanded, setAccordionExpanded] = useState(flowTaskTabNames.ABOUT)

  let errorFieldsBinding = []
  errorFieldsBinding[flowTaskTabNames.TASK_ASSIGN] = ['task_id']
  errorFieldsBinding[flowTaskTabNames.ABOUT] = ['name', 'description', 'uninterrupted']
  errorFieldsBinding[flowTaskTabNames.PARAMETERS] = [
    'planned_duration',
    'contingency_duration',
    'contingency_type',
    'reference_duration',
    // -- design
    'weather_data_id',
    'hs_design',
    'wind_speed_design',
    'current_speed_design',
    'custom_criteria_design',
    // -- operational
    'group_name',
    'hs_operational',
    'hs_calculation_method',
    'hs_calculation_table_id',
    'wind_speed_operational',
    'wind_speed_calculation_method',
    'wind_speed_calculation_table_id',
    'current_speed_operational',
    'current_speed_calculation_method',
    'custom_criteria_operational',
    'custom_criteria_calculation_method'
  ]
  errorFieldsBinding[flowTaskTabNames.GEOGRAPHY] = [
    'location_start_id',
    'location_finish_id'
    // 'resource_impacted',
  ]

  // -- errors handlers by tabs
  // errors['about'][fieldName] = ['errors message']
  // errors['parameters'][fieldName] = ['errors message']
  const [errors, setErrors] = useState(null)

  // -- Find the flow element action from the flowGroup data.
  // let _currentTaskInstance = []
  // ;(currentFlowGroup?.flows || [])
  //   .map((flow) => (flow?.taskInstances || []).map((el) => (el?.element_id === currentElement.id ? el : null)))
  //   .filter((el) => el && el !== null && typeof el !== 'undefined' && el.length > 0)
  //   .forEach((el) => {
  //     if (el && typeof el === 'object') {
  //       el.forEach((_el) => {
  //         if (_el) {
  //           _currentTaskInstance.push(_el)
  //         }
  //       })
  //     }
  //   })
  // const __currentTaskInstance = _currentTaskInstance.find((el) => el && el.id) || null
  const flowGroups = useSelector((state) => state.flowGroups)
  const flowGroupTaskInstances = flowGroups?.flowGroupTasks.get(currentFlowGroup.id).instances || null
  const currentTaskInstance = flowGroupTaskInstances?.get(currentElement.id) || null

  useEffect(() => {
    Logger.trace(LoggerTypes.CONTROLLER, 'FlowElementTaskTab.useEffect(...) CHANGED', {
      flowGroups,
      currentFlowGroup,
      currentTaskInstance,
      currentElementId: currentElement.id
    })

    setTaskInstanceTemp(currentTaskInstance)
    setErrors(null)
  }, [currentElement, currentFlowGroup])

  // useEffect(() => {
  // Logger.trace(LoggerTypes.CONTROLLER, 'FlowElementTaskTab.useEffect(...) INIT', { currentTaskInstance })
  // setCurrentTaskInstance(__currentTaskInstance)
  // }, [])

  // Each time something is updated by the user in any tab or accordion, we refresh the taskInstanceTemp
  // which is used to display the current (non-submitted) values of the current selected BPMN element
  const _getTaskInstanceTemp = () => {
    return {
      ...taskInstanceTemp,
      checks: currentTaskInstance ? [...currentTaskInstance?.checks] : [],
      resource_interactions: currentTaskInstance ? [...currentTaskInstance?.resource_interactions] : [],
      variable_interactions: currentTaskInstance ? [...currentTaskInstance?.variable_interactions] : []
    }
  }

  const refreshTaskInstanceTemp = () => {
    setTaskInstanceTemp(_getTaskInstanceTemp())
  }
  const _currentTaskInstance = taskInstanceTemp ? _getTaskInstanceTemp() : currentTaskInstance

  const onTabChange = (e, newTabValue) => {
    setTabValue(newTabValue)
    // setCurrentTaskInstance(__currentTaskInstance)
    // setTaskInstanceTemp(currentTaskInstance)
    refreshTaskInstanceTemp()
    Logger.trace(LoggerTypes.CONTROLLER, 'FlowElementTaskTab.onTabChange()', { newTabValue })
  }

  const onChangeAccordion = (panel) => (e, newExpanded) => {
    setAccordionExpanded(newExpanded ? panel : false)
    // setCurrentTaskInstance(currentTaskInstance)
    refreshTaskInstanceTemp()
    Logger.trace(LoggerTypes.CONTROLLER, 'FlowElementTaskTab.onChangeAccordion()', {
      newExpanded
    })
  }

  const onSaveAssignTask = (assign_task_id, onComplete) => {
    const pid = currentProject?.id || null
    const sid = currentScenario?.id || null
    const fid = currentFlowGroup?.id || null
    const tid = currentTaskInstance.id || null

    Logger.trace(LoggerTypes.CONTROLLER, 'FlowElementTaskTab.onSaveAssignTask()', {
      pid,
      sid,
      fid,
      tid,
      assign_task_id
    })

    dispatch(assignFlowTask(pid, sid, fid, tid, assign_task_id))
      .then((res) => {
        Logger.trace(
          LoggerTypes.CONTROLLER,
          'FlowElementTaskTab.onSaveAssignTask().dispatch(assignFlowTask(...)).then()',
          {
            taskInstanceId: tid,
            targetTaskId: assign_task_id,
            res
          }
        )
        // checkUpdateResponse(res, enqueueSnackbar, t)
        if (onComplete && typeof onComplete === 'function') {
          onComplete(res)
        }

        if (refreshAfterSave && typeof refreshAfterSave === 'function') {
          refreshAfterSave(currentFlowGroup, [res.data])
        }
      })
      .catch((res) => {
        Logger.trace(LoggerTypes.WARN, 'FlowElementTaskTab.onSaveAssignTask().dispatch(assignFlowTask(...)).catch()', {
          res
        })
        checkErrorResponse(res, enqueueSnackbar, t)

        // -- set the error to the corresponding tab
        if (res?.response?.data?.fields) {
          const errorFields = res.response.data.fields

          let _tmpError = []
          _tmpError[flowTaskTabNames.TASK_ASSIGN] = []

          const findTabNameFromFieldName = (fieldName) => {
            let __tabName = null
            Object.keys(errorFieldsBinding).forEach((_tabName) =>
              (errorFieldsBinding[_tabName] || []).forEach((_field) => {
                if (fieldName === _field) {
                  __tabName = _tabName
                }
              })
            )
            return __tabName
          }

          Object.keys(errorFields).forEach((rootErr) => {
            let tbName
            // -- check errors at root
            tbName = findTabNameFromFieldName(rootErr)
            if (tbName) {
              _tmpError[tbName][rootErr] = [
                errorFields[rootErr]?._schema ? errorFields[rootErr]._schema : errorFields[rootErr]
              ]
            }
          })

          Logger.trace(LoggerTypes.WARN, 'FlowElementTaskTab.onSaveTask().dispatch(assignFlowTask(...)).catch()', {
            errorFields,
            errorFieldsBinding,
            _tmpError
          })

          setErrors(_tmpError)
        }
      })
  }

  const onSaveTask = (onComplete) => {
    const pid = currentProject?.id || null
    const sid = currentScenario?.id || null
    const fid = currentFlowGroup?.id || null

    // -- All API with PUT methods does not need ids (because they are in the URL)
    // -- We clean the current task id and reference_duration which is a calculated field
    const taskInstance = {
      ..._currentTaskInstance
    }
    delete taskInstance.checks
    delete taskInstance.resource_interactions
    delete taskInstance.variable_interactions
    taskInstance.task.id = null
    taskInstance.task.reference_duration = null

    Logger.trace(LoggerTypes.CONTROLLER, 'FlowElementTaskTab.onSaveTask()', {
      pid,
      sid,
      fid,
      currentTaskInstance,
      _currentTaskInstance,
      taskInstance
    })

    dispatch(updateFlowTask(pid, sid, fid, taskInstance))
      .then((res) => {
        Logger.trace(LoggerTypes.CONTROLLER, 'FlowElementTaskTab.onSaveTask().dispatch(updateFlowTask(...)).then()', {
          res,
          refreshAfterSave
        })

        if (res?.data && onComplete && typeof onComplete === 'function') {
          Logger.trace(LoggerTypes.CONTROLLER, 'FlowElementTaskTab.onSaveTask().onComplete()')
          onComplete(res.data)
        }
        checkUpdateResponse(res, enqueueSnackbar, t)

        // -- remove the previous errors
        setErrors(null)

        // Now the task has been updated in the back-end we propagate the change in the REDUX state cache
        dispatch(cacheUpdateFlowGroupTask(currentFlowGroup, res.data))

        // -- reload the FlowGroups data.
        if (refreshAfterSave && typeof refreshAfterSave === 'function') {
          refreshAfterSave(currentFlowGroup, [res.data])
        }
      })
      .catch((res) => {
        Logger.trace(LoggerTypes.WARN, 'FlowElementTaskTab.onSaveTask().dispatch(updateFlowTask(...)).catch()', { res })
        checkErrorResponse(res, enqueueSnackbar, t)

        // -- set the error to the corresponding tab
        if (res?.response?.data?.fields) {
          const errorFields = res.response.data.fields

          let _tmpError = []
          _tmpError[flowTaskTabNames.ABOUT] = []
          _tmpError[flowTaskTabNames.PARAMETERS] = []
          _tmpError[flowTaskTabNames.GEOGRAPHY] = []

          const findTabNameFromFieldName = (fieldName) => {
            let __tabName = null
            Object.keys(errorFieldsBinding).forEach((_tabName) =>
              (errorFieldsBinding[_tabName] || []).forEach((_field) => {
                if (fieldName === _field) {
                  __tabName = _tabName
                }
              })
            )
            return __tabName
          }

          Object.keys(errorFields).forEach((rootErr) => {
            let tbName
            // -- check errors in `task` attribute
            if (rootErr === flowElementTypes.TASK) {
              Object.keys(errorFields.task).forEach((taskErr) => {
                tbName = findTabNameFromFieldName(taskErr)
                if (tbName) {
                  _tmpError[tbName][taskErr] = [
                    errorFields.task[taskErr]?._schema ? errorFields.task[taskErr]._schema : errorFields.task[taskErr]
                  ]
                }
              })
            } else {
              // -- check errors at root
              tbName = findTabNameFromFieldName(rootErr)
              if (tbName) {
                _tmpError[tbName][rootErr] = [
                  errorFields[rootErr]?._schema ? errorFields[rootErr]._schema : errorFields[rootErr]
                ]
              }
            }
          })

          Logger.trace(LoggerTypes.WARN, 'FlowElementTaskTab.onSaveTask().dispatch(updateFlowTask(...)).catch()', {
            errorFields,
            errorFieldsBinding,
            _tmpError
          })

          setErrors(_tmpError)
        }
      })
  }

  const onChangeTask = (tempTask) => {
    const taskInstance = {
      ..._currentTaskInstance,
      ...tempTask
    }

    Logger.trace(LoggerTypes.CONTROLLER, 'FlowElementTaskTab.onChangeTask()', {
      currentTaskInstance,
      _currentTaskInstance,
      tempTask
    })

    setTaskInstanceTemp(taskInstance)
  }

  const countErrorsTaskAssign =
    errors && errors[flowTaskTabNames.TASK_ASSIGN] && Object.keys(errors[flowTaskTabNames.TASK_ASSIGN]).length
  const countErrorsAbout =
    errors && errors[flowTaskTabNames.ABOUT] && Object.keys(errors[flowTaskTabNames.ABOUT]).length
  const countErrorsParameters =
    errors && errors[flowTaskTabNames.PARAMETERS] && Object.keys(errors[flowTaskTabNames.PARAMETERS]).length
  const countErrorsGeography =
    errors && errors[flowTaskTabNames.GEOGRAPHY] && Object.keys(errors[flowTaskTabNames.GEOGRAPHY]).length

  Logger.trace(LoggerTypes.CONTAINER, 'FlowElementTaskTab.render()', {
    tabValue,
    currentProject,
    currentScenario,
    currentFlowGroup,
    currentElement,
    // currentTaskInstance,
    // _currentTaskInstance,
    currentTaskInstance,
    _currentTaskInstance,
    taskInstanceTemp,
    accordionExpanded,
    // -- Scenario's attributes
    attributes: {
      variables,
      resources,
      locations,
      products,
      weather_data
      // products,
      // parameters
    },
    flowGroups,
    flowActions: {
      alphaFactors,
      tasks
      // conditions
    },
    errors: {
      errors,
      countErrorsAbout,
      countErrorsParameters,
      countErrorsGeography
    }
  })

  return (
    (currentProject && currentScenario && currentFlowGroup && _currentTaskInstance && (
      <div className="FlowElementTask FlowElementTaskTab">
        <DebugView type={pageTypes.FLOW}>{_currentTaskInstance}</DebugView>
        <TabContext value={`${tabValue}`}>
          <Tabs centered={true} value={tabValue} onChange={onTabChange} style={{ position: 'relative' }}>
            <Tooltip ref={ref} title={t('flowActions.tasks.tabs.task_assign')}>
              <Tab disableRipple={true} id="simple-tab-0" icon={<Icon name={iconNames.TARGET} size={20} />} />
            </Tooltip>

            <Tooltip ref={ref} title={t('flowActions.tasks.tabs.parameters')}>
              <Tab disableRipple={true} id="simple-tab-1" icon={<Icon name={iconNames.PARAMETER} size={20} />} />
            </Tooltip>

            <Tooltip ref={ref} title={t('flowActions.tasks.tabs.interactions')}>
              <Tab disableRipple={true} id="simple-tab-2" icon={<Icon name={iconNames.INTERACTION} size={20} />} />
            </Tooltip>
          </Tabs>

          {/*  ======== TAB: TASK IDENTITY ========================  */}
          <TabPanel value={tabValue} index={0}>
            {/*  -- TASK ASSIGN --  */}
            <Accordion
              expanded={accordionExpanded === flowTaskTabNames.TASK_ASSIGN}
              onChange={onChangeAccordion(flowTaskTabNames.TASK_ASSIGN)}
            >
              <AccordionSummary
                aria-controls={`${flowTaskTabNames.TASK_ASSIGN}-content`}
                id={`${flowTaskTabNames.TASK_ASSIGN}-header`}
                className={`${countErrorsTaskAssign > 0 ? styleClasses.accordionError : ''}`}
              >
                <Typography>
                  <Icon name={iconNames.ASSIGN} size={12} />
                  <span className="iconAccordion">{t('flowActions.tasks.panels.task_assign.title')}</span>
                </Typography>
                {countErrorsTaskAssign > 0 && (
                  <i className={`errorPin ${styleClasses.pinError}`}>{countErrorsTaskAssign}</i>
                )}
              </AccordionSummary>
              <AccordionDetails>
                <FlowElementTaskAssignPanel
                  currentProject={currentProject}
                  currentScenario={currentScenario}
                  currentFlowGroup={currentFlowGroup}
                  currentTaskInstance={_currentTaskInstance}
                  tasks={tasks}
                  onSaveAssignTask={onSaveAssignTask}
                  errors={(errors && errors[flowTaskTabNames.TASK_ASSIGN]) || null}
                />
              </AccordionDetails>
            </Accordion>
          </TabPanel>

          {/*  ======== TAB: PARAMETERS ========================  */}
          <TabPanel value={tabValue} index={1}>
            {/*  -- ABOUT --  */}
            <Accordion
              expanded={accordionExpanded === flowTaskTabNames.ABOUT}
              onChange={onChangeAccordion(flowTaskTabNames.ABOUT)}
            >
              <AccordionSummary
                aria-controls={`${flowTaskTabNames.ABOUT}-content`}
                id={`${flowTaskTabNames.ABOUT}-header`}
                className={`${countErrorsAbout > 0 ? styleClasses.accordionError : ''}`}
              >
                <Typography>
                  <Icon name={iconNames.ABOUT} size={12} />
                  <span className="iconAccordion">{t('flowActions.tasks.panels.about.title')}</span>
                </Typography>
                {countErrorsAbout > 0 && <i className={`errorPin ${styleClasses.pinError}`}>{countErrorsAbout}</i>}
              </AccordionSummary>
              <AccordionDetails>
                <FlowElementTaskAboutPanel
                  currentTaskInstance={_currentTaskInstance}
                  onSaveTask={onSaveTask}
                  onChangeTask={onChangeTask}
                  errors={(errors && errors[flowTaskTabNames.ABOUT]) || null}
                />
              </AccordionDetails>
            </Accordion>

            {/*  -- PARAMETERS --  */}
            <Accordion
              expanded={accordionExpanded === flowTaskTabNames.PARAMETERS}
              onChange={onChangeAccordion(flowTaskTabNames.PARAMETERS)}
            >
              <AccordionSummary
                aria-controls={`${flowTaskTabNames.PARAMETERS}-content`}
                id={`${flowTaskTabNames.PARAMETERS}-header`}
                className={`${countErrorsParameters > 0 ? styleClasses.accordionError : ''}`}
              >
                <Typography>
                  <Icon name={iconNames.PARAMETER} size={12} />
                  <span className="iconAccordion">{t('flowActions.tasks.panels.parameters.title')}</span>
                </Typography>
                {countErrorsParameters > 0 && (
                  <i className={`errorPin ${styleClasses.pinError}`}>{countErrorsParameters}</i>
                )}
              </AccordionSummary>
              <AccordionDetails>
                <FlowElementTaskParameterPanel
                  currentTaskInstance={_currentTaskInstance}
                  alphaFactors={alphaFactors}
                  weather_data={weather_data}
                  onSaveTask={onSaveTask}
                  onChangeTask={onChangeTask}
                  errors={(errors && errors[flowTaskTabNames.PARAMETERS]) || null}
                />
              </AccordionDetails>
            </Accordion>

            {/*  -- GEOGRAPHY --  */}
            <Accordion
              expanded={accordionExpanded === flowTaskTabNames.GEOGRAPHY}
              onChange={onChangeAccordion(flowTaskTabNames.GEOGRAPHY)}
            >
              <AccordionSummary
                aria-controls={`${flowTaskTabNames.GEOGRAPHY}-content`}
                id={`${flowTaskTabNames.GEOGRAPHY}-header`}
                className={`${countErrorsGeography > 0 ? styleClasses.accordionError : ''}`}
              >
                <Typography>
                  <Icon name={iconNames.LOCATION} size={12} />
                  <span className="iconAccordion">{t('flowActions.tasks.panels.geography.title')}</span>
                </Typography>
                {countErrorsGeography > 0 && (
                  <i className={`errorPin ${styleClasses.pinError}`}>{countErrorsGeography}</i>
                )}
              </AccordionSummary>
              <AccordionDetails>
                <FlowElementTaskGeographyPanel
                  currentTaskInstance={_currentTaskInstance}
                  locations={locations}
                  products={products}
                  onSaveTask={onSaveTask}
                  onChangeTask={onChangeTask}
                  errors={(errors && errors[flowTaskTabNames.GEOGRAPHY]) || null}
                />
              </AccordionDetails>
            </Accordion>
          </TabPanel>

          {/*  ======== TAB: INTERACTION ========================  */}
          <TabPanel value={tabValue} index={2}>
            {/*  -- INTERACTION_CONDITIONS --  */}
            <Accordion
              expanded={accordionExpanded === flowTaskTabNames.INTERACTION_CONDITIONS}
              onChange={onChangeAccordion(flowTaskTabNames.INTERACTION_CONDITIONS)}
            >
              <AccordionSummary
                aria-controls={`${flowTaskTabNames.INTERACTION_CONDITIONS}-content`}
                id={`${flowTaskTabNames.INTERACTION_CONDITIONS}-header`}
              >
                <Typography>
                  <Icon name={iconNames.CONDITION} size={12} />
                  <span className="iconAccordion">{t('flowActions.tasks.panels.interactions.conditions.title')}</span>
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <FlowElementTaskInteractionCheckPanel
                  currentProject={currentProject}
                  currentScenario={currentScenario}
                  currentFlowGroup={currentFlowGroup}
                  currentTaskInstance={_currentTaskInstance}
                  resources={resources}
                  variables={variables}
                  parameters={parameters}
                  // refreshAfterSaving={refreshAfterSaving}
                />
              </AccordionDetails>
            </Accordion>

            {/*  -- INTERACTION_RESOURCES --  */}
            <Accordion
              expanded={accordionExpanded === flowTaskTabNames.INTERACTION_RESOURCES}
              onChange={onChangeAccordion(flowTaskTabNames.INTERACTION_RESOURCES)}
            >
              <AccordionSummary
                aria-controls={`${flowTaskTabNames.INTERACTION_RESOURCES}-content`}
                id={`${flowTaskTabNames.INTERACTION_RESOURCES}-header`}
              >
                <Typography>
                  <Icon name={iconNames.RESOURCE} size={12} />
                  <span className="iconAccordion">{t('flowActions.tasks.panels.interactions.resources.title')}</span>
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <FlowElementTaskInteractionResourcesPanel
                  currentProject={currentProject}
                  currentScenario={currentScenario}
                  currentFlowGroup={currentFlowGroup}
                  currentTaskInstance={_currentTaskInstance}
                  resources={resources}
                  // refreshAfterSaving={refreshAfterSaving}
                />
              </AccordionDetails>
            </Accordion>

            {/*  -- INTERACTION_VARIABLES --  */}
            <Accordion
              expanded={accordionExpanded === flowTaskTabNames.INTERACTION_VARIABLES}
              onChange={onChangeAccordion(flowTaskTabNames.INTERACTION_VARIABLES)}
            >
              <AccordionSummary
                aria-controls={`${flowTaskTabNames.INTERACTION_VARIABLES}-content`}
                id={`${flowTaskTabNames.INTERACTION_VARIABLES}-header`}
              >
                <Typography>
                  <Icon name={iconNames.VARIABLE} size={12} />
                  <span className="iconAccordion">{t('flowActions.tasks.panels.interactions.variables.title')}</span>
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <FlowElementTaskInteractionVariablesPanel
                  currentProject={currentProject}
                  currentScenario={currentScenario}
                  currentFlowGroup={currentFlowGroup}
                  currentTaskInstance={_currentTaskInstance}
                  variables={variables}
                  // refreshAfterSaving={refreshAfterSaving}
                />
              </AccordionDetails>
            </Accordion>
          </TabPanel>
        </TabContext>
      </div>
    )) || <div></div>
  )
}

FlowElementTaskTab.displayName = 'FlowElementTaskTab'

FlowElementTaskTab.propTypes = {
  currentProject: PropTypes.object.isRequired,
  currentScenario: PropTypes.object.isRequired,
  currentFlowGroup: PropTypes.object.isRequired,
  currentElement: PropTypes.object,
  // -- Scenario's Attributes
  resources: PropTypes.arrayOf(PropTypes.object),
  variables: PropTypes.arrayOf(PropTypes.object),
  locations: PropTypes.arrayOf(PropTypes.object),
  products: PropTypes.arrayOf(PropTypes.object),
  weather_data: PropTypes.arrayOf(PropTypes.object),
  parameters: PropTypes.arrayOf(PropTypes.object),
  // products: PropTypes.arrayOf(PropTypes.object),
  // -- Flow's Action + AlphaFactors
  alphaFactors: PropTypes.object,
  tasks: PropTypes.arrayOf(PropTypes.object),
  // -- refresh after saving
  refreshAfterSave: PropTypes.func
}

FlowElementTaskTab.defaultProps = {
  currentProject: null,
  currentScenario: null,
  currentFlowGroup: null,
  currentElement: null,
  // -- Scenario's Attributes
  resources: [],
  variables: [],
  locations: [],
  products: [],
  weather_data: [],
  parameters: [],
  // products: [],
  // -- Flow's Action + AlphaFactors
  alphaFactors: [],
  tasks: [],
  // -- refresh after saving
  refreshAfterSave: null
}
